import React, {useState} from 'react';
import {Modal, Textarea} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {DateInput} from '@mantine/dates';
import {Button} from 'src/design/atoms/Button';

export interface ComplianceBulkWaiveDialogProps {
  opened: boolean;
  selectedCount: number;
  isSaving: boolean;
  onClose: () => void;
  onConfirm: (payload: {reason: string; expiresAt?: Date}) => void;
}

/**
 * Waive dialog for the compliance bulk bar. Reason is required; expiry is
 * optional. Waiving creates rule exceptions for the selected users' matching
 * rules — a background job then deactivates their non-completed assignments,
 * so the copy spells out that consequence up front.
 */
export const ComplianceBulkWaiveDialog: React.FC<
  ComplianceBulkWaiveDialogProps
> = ({opened, selectedCount, isSaving, onClose, onConfirm}) => {
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  const handleClose = () => {
    setReason('');
    setExpiresAt(null);
    onClose();
  };

  const canConfirm = reason.trim().length > 0;

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }
    onConfirm({
      reason: reason.trim(),
      expiresAt: expiresAt ?? undefined,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Waive ${selectedCount} assignment${selectedCount === 1 ? '' : 's'}?`}
      centered>
      <Text size="sm" c="grey" mb="lg">
        Waived people are exempted from the matching assignment rules and
        their open assignments are closed in the background; completed
        records are kept. Users with no matching rule coverage are skipped.
      </Text>
      <Textarea
        label="Reason"
        placeholder="Explain why this waiver is being granted"
        required
        value={reason}
        onChange={event => setReason(event.currentTarget.value)}
        minRows={3}
        mb="lg"
      />
      <DateInput
        label="Expires (optional)"
        placeholder="Never"
        value={expiresAt}
        onChange={setExpiresAt}
        clearable
        mb="lg"
      />
      <Group justify="end">
        <Button variant="subtle" color="black" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="filled"
          color="red"
          onClick={handleConfirm}
          disabled={!canConfirm || isSaving}
          loading={isSaving}>
          Waive
        </Button>
      </Group>
    </Modal>
  );
};
